/* eslint-disable */
/**
 * BeregnerDataPanel.jsx — Anonymiseret beregner-statistik i OS-dashboard
 *
 * Datakilde: GET /api/os/beregner/statistik
 * Poll-interval: 120 sekunder
 *
 * Sektioner:
 *   1. KPI-kort  — Total / Seneste 7 dage / Seneste 30 dage / Første beregning
 *   2. Type-fordeling — badges med tæller per beregnertype
 *   3. Tidsserie — Recharts LineChart (seneste 30 dage, alle typer)
 *   4. Indsigter — 3 låste kort, låses op ved >= 10 datapunkter
 *
 * GDPR: Ingen individuelle data vises. Kun aggregater.
 * Indsigter låses bag tærskel for at undgå re-identifikation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from 'recharts';
import { BarChart2, Lock, TrendingUp, Users, Calendar, Clock } from 'lucide-react';

// ─── Design tokens (matcher OSDashboardNy) ────────────────────────────────────
const C = {
  bg:        '#060917',
  kort:      'rgba(8,12,30,0.60)',
  kant:      'rgba(255,255,255,0.08)',
  tekst:     '#e2e8f0',
  subtekst:  '#7a8fa6',
  success:   '#22c55e',
  warning:   '#f59e0b',
  danger:    '#ef4444',
  info:      '#3b82f6',
  lilla:     '#8b5cf6',
  cyan:      '#3fd4c7',
};

const TYPE_FARVER = {
  boernebidrag:  '#3b82f6',
  boligstoette:  '#22c55e',
  friplads:      '#f59e0b',
  tilskud:       '#8b5cf6',
};

const TYPE_LABELS = {
  boernebidrag: 'Børnebidrag',
  boligstoette: 'Boligstøtte',
  friplads:     'Friplads',
  tilskud:      'Tilskud',
};

// ─── Hjælpefunktioner ────────────────────────────────────────────────────────

function formatDato(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}

function formatKortDato(isoStr) {
  if (!isoStr) return '';
  // isoStr = 'YYYY-MM-DD'
  const parts = isoStr.split('-');
  if (parts.length < 3) return isoStr;
  const mdr = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];
  const m = parseInt(parts[1], 10) - 1;
  return `${parseInt(parts[2], 10)}. ${mdr[m] || parts[1]}`;
}

// ─── Boks-wrapper ─────────────────────────────────────────────────────────────
function Boks({ children, style = {} }) {
  return (
    <div style={{
      background:           C.kort,
      border:               `1px solid ${C.kant}`,
      borderRadius:         12,
      backdropFilter:       'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      padding:              '18px 20px',
      ...style,
    }}>
      {children}
    </div>
  );
}

// ─── KPI-kort ─────────────────────────────────────────────────────────────────
function KpiKort({ label, value, farve = C.info, ikon: Ikon }) {
  return (
    <div style={{
      flex:        '1 1 160px',
      minWidth:    140,
      background:  C.kort,
      border:      `1px solid ${C.kant}`,
      borderRadius: 12,
      padding:     '16px 18px',
      backdropFilter: 'blur(16px)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        {Ikon && <Ikon size={14} color={farve} />}
        <span style={{ fontSize: 12, color: C.subtekst, fontWeight: 600, letterSpacing: 0.3 }}>{label}</span>
      </div>
      <div style={{ fontSize: 30, fontWeight: 700, color: farve }}>{value ?? '—'}</div>
    </div>
  );
}

// ─── Indsigt-kort (låsbar) ────────────────────────────────────────────────────
function IndsigtsKort({ titel, vaerdi, enhed, laast, fremskridt, taerskel }) {
  if (laast) {
    const pct = Math.round((fremskridt / taerskel) * 100);
    return (
      <div style={{
        flex:        '1 1 180px',
        minWidth:    160,
        background:  'rgba(8,12,30,0.40)',
        border:      '1px solid rgba(255,255,255,0.06)',
        borderRadius: 12,
        padding:     '16px 18px',
        position:    'relative',
        overflow:    'hidden',
      }}>
        {/* Låseikon */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <Lock size={12} color={C.subtekst} />
          <span style={{ fontSize: 11, color: C.subtekst, fontWeight: 600 }}>{titel}</span>
        </div>
        {/* Fremskridtsindikator */}
        <div style={{ fontSize: 11, color: C.subtekst, marginBottom: 6 }}>
          {fremskridt} / {taerskel} beregnere
        </div>
        <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 5, overflow: 'hidden' }}>
          <div style={{
            width:      `${pct}%`,
            height:     '100%',
            background: `linear-gradient(90deg, ${C.cyan}, ${C.info})`,
            borderRadius: 4,
            minWidth:   pct > 0 ? 4 : 0,
          }} />
        </div>
        <div style={{ fontSize: 10, color: C.subtekst, marginTop: 4 }}>
          Låses op ved {taerskel} datapunkter
        </div>
      </div>
    );
  }

  return (
    <div style={{
      flex:        '1 1 180px',
      minWidth:    160,
      background:  C.kort,
      border:      `1px solid ${C.kant}`,
      borderRadius: 12,
      padding:     '16px 18px',
    }}>
      <div style={{ fontSize: 11, color: C.subtekst, fontWeight: 600, marginBottom: 10 }}>{titel}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: C.tekst }}>
        {vaerdi ?? '—'}
        {enhed && <span style={{ fontSize: 13, color: C.subtekst, marginLeft: 4 }}>{enhed}</span>}
      </div>
    </div>
  );
}

// ─── Tidsserie data-transformer ──────────────────────────────────────────────
function buildChartData(tidsserie) {
  if (!tidsserie || tidsserie.length === 0) return [];

  // Saml alle datoer
  const datoMap = {};
  tidsserie.forEach(({ dato, beregner_type, antal }) => {
    if (!datoMap[dato]) datoMap[dato] = { dato };
    datoMap[dato][beregner_type] = parseInt(antal, 10);
  });

  return Object.values(datoMap)
    .sort((a, b) => a.dato.localeCompare(b.dato))
    .map(row => ({ ...row, _label: formatKortDato(row.dato) }));
}

// ─── Hoveddkomponent ─────────────────────────────────────────────────────────
export default function BeregnerDataPanel() {
  const [data,    setData]    = useState(null);
  const [loader,  setLoader]  = useState(true);
  const [fejl,    setFejl]    = useState(null);
  const [sidstOp, setSidstOp] = useState(null);

  const hent = useCallback(async () => {
    try {
      const r = await fetch('/api/os/beregner/statistik', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setData(d);
      setFejl(null);
      setSidstOp(new Date());
    } catch (err) {
      setFejl(err.message);
    } finally {
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    hent();
    const id = setInterval(hent, 120_000);
    return () => clearInterval(id);
  }, [hent]);

  // ── Loader ────────────────────────────────────────────────────────────────
  if (loader) {
    return (
      <div style={{ padding: 40, color: C.subtekst, fontSize: 14 }}>
        Henter beregner-statistik…
      </div>
    );
  }

  // ── Fejl ──────────────────────────────────────────────────────────────────
  if (fejl) {
    return (
      <Boks style={{ borderTop: `3px solid ${C.danger}` }}>
        <div style={{ fontSize: 13, color: C.danger }}>Kunne ikke hente statistik: {fejl}</div>
      </Boks>
    );
  }

  // ── Tom tilstand (ingen data endnu) ───────────────────────────────────────
  if (!data?.har_data) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <BarChart2 size={48} color={C.subtekst} style={{ marginBottom: 16 }} />
        <div style={{ fontSize: 18, fontWeight: 700, color: C.tekst, marginBottom: 8 }}>
          Ingen beregner-data endnu
        </div>
        <div style={{ fontSize: 13, color: C.subtekst, maxWidth: 400, margin: '0 auto' }}>
          Statistik vises automatisk når brugere begynder at anvende beregnerne.
          Data er anonymiseret — ingen individuelle beregninger gemmes med personoplysninger.
        </div>
      </div>
    );
  }

  const chartData     = buildChartData(data.tidsserie);
  const aktivTyper    = Object.keys(TYPE_FARVER).filter(t => parseInt(data[t] ?? 0, 10) > 0);
  const laast         = data.indsigter_laast;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, color: C.tekst, fontSize: 22, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 10 }}>
            <BarChart2 size={22} color={C.cyan} />
            Beregner-data
          </h2>
          <div style={{ fontSize: 12, color: C.subtekst, marginTop: 3 }}>
            Anonymiseret statistik — ingen individuelle data
          </div>
        </div>
        {sidstOp && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.subtekst }}>
            <Clock size={11} />
            Opdateret {sidstOp.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>

      {/* ── KPI-kort ────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <KpiKort
          label="Total beregnere"
          value={parseInt(data.total, 10).toLocaleString('da-DK')}
          farve={C.cyan}
          ikon={BarChart2}
        />
        <KpiKort
          label="Seneste 7 dage"
          value={parseInt(data.seneste_7_dage, 10).toLocaleString('da-DK')}
          farve={C.success}
          ikon={TrendingUp}
        />
        <KpiKort
          label="Seneste 30 dage"
          value={parseInt(data.seneste_30_dage, 10).toLocaleString('da-DK')}
          farve={C.info}
          ikon={Users}
        />
        <KpiKort
          label="Første beregning"
          value={formatDato(data.foerste_beregning)}
          farve={C.subtekst}
          ikon={Calendar}
        />
      </div>

      {/* ── Type-fordeling ───────────────────────────────────────────────────── */}
      <Boks>
        <div style={{ fontSize: 12, color: C.subtekst, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>
          Fordeling per type
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {Object.keys(TYPE_LABELS).map(type => {
            const antal = parseInt(data[type] ?? 0, 10);
            const pct   = data.total > 0 ? Math.round((antal / parseInt(data.total, 10)) * 100) : 0;
            return (
              <div key={type} style={{
                display:      'flex',
                alignItems:   'center',
                gap:          10,
                background:   `${TYPE_FARVER[type]}18`,
                border:       `1px solid ${TYPE_FARVER[type]}40`,
                borderRadius: 8,
                padding:      '10px 16px',
                minWidth:     130,
                flex:         '1 1 130px',
              }}>
                <div style={{
                  width:        10,
                  height:       10,
                  borderRadius: '50%',
                  background:   TYPE_FARVER[type],
                  flexShrink:   0,
                  boxShadow:    `0 0 6px ${TYPE_FARVER[type]}`,
                }} />
                <div>
                  <div style={{ fontSize: 13, color: C.tekst, fontWeight: 700 }}>
                    {antal.toLocaleString('da-DK')}
                  </div>
                  <div style={{ fontSize: 11, color: C.subtekst }}>
                    {TYPE_LABELS[type]} &nbsp;
                    <span style={{ color: TYPE_FARVER[type] }}>{pct}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Boks>

      {/* ── Tidsserie-graf ─────────────────────────────────────────────────── */}
      {chartData.length > 0 && (
        <Boks>
          <div style={{ fontSize: 12, color: C.subtekst, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 18 }}>
            Aktivitet — seneste 30 dage
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="_label"
                tick={{ fill: C.subtekst, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fill: C.subtekst, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                width={28}
              />
              <Tooltip
                contentStyle={{
                  background:   'rgba(8,12,30,0.95)',
                  border:       `1px solid ${C.kant}`,
                  borderRadius: 8,
                  fontSize:     12,
                  color:        C.tekst,
                }}
                labelStyle={{ color: C.subtekst }}
                itemStyle={{ color: C.tekst }}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, color: C.subtekst, paddingTop: 8 }}
              />
              {aktivTyper.map(type => (
                <Line
                  key={type}
                  type="monotone"
                  dataKey={type}
                  name={TYPE_LABELS[type]}
                  stroke={TYPE_FARVER[type]}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Boks>
      )}

      {/* ── Indsigter ────────────────────────────────────────────────────────── */}
      <Boks>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: C.subtekst, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
            Aggregerede indsigter
          </div>
          {laast && (
            <div style={{
              display:      'flex',
              alignItems:   'center',
              gap:          4,
              fontSize:     10,
              color:        C.warning,
              background:   'rgba(245,158,11,0.10)',
              border:       '1px solid rgba(245,158,11,0.25)',
              borderRadius: 6,
              padding:      '2px 8px',
              fontWeight:   700,
            }}>
              <Lock size={9} />
              LÅST — {data.indsigter_fremskridt}/{data.indsigter_taerskel} datapunkter
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>

          {/* Indsigt 1: Gns. børnebidrag per måned */}
          <IndsigtsKort
            titel="Gns. børnebidrag / md."
            vaerdi={
              !laast && data.indsigter?.boernebidrag?.gns_bidrag_md != null
                ? `${parseInt(data.indsigter.boernebidrag.gns_bidrag_md, 10).toLocaleString('da-DK')} kr`
                : null
            }
            laast={laast}
            fremskridt={data.indsigter_fremskridt}
            taerskel={data.indsigter_taerskel}
          />

          {/* Indsigt 2: Andel berettiget til boligstøtte */}
          <IndsigtsKort
            titel="Berettiget til boligstøtte"
            vaerdi={
              !laast && data.indsigter?.boligstoette?.pct_berettiget != null
                ? `${data.indsigter.boligstoette.pct_berettiget}%`
                : null
            }
            laast={laast}
            fremskridt={data.indsigter_fremskridt}
            taerskel={data.indsigter_taerskel}
          />

          {/* Indsigt 3: Gns. ydelser for enlig forsørger */}
          <IndsigtsKort
            titel="Gns. ydelser (enlig forsørger) / md."
            vaerdi={
              !laast && data.indsigter?.tilskud?.gns_total_md != null
                ? `${parseInt(data.indsigter.tilskud.gns_total_md, 10).toLocaleString('da-DK')} kr`
                : null
            }
            laast={laast}
            fremskridt={data.indsigter_fremskridt}
            taerskel={data.indsigter_taerskel}
          />

        </div>
      </Boks>

    </div>
  );
}
